
import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Search, Filter, Briefcase, Calculator, CheckCircle,
  AlertTriangle, DollarSign, Calendar, Eye, ChevronRight, X, User,
  TrendingUp, CreditCard, Mail, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Pencil
} from 'lucide-react';
import { Button, Input, Badge, Card, Modal, FieldHint, InfoTooltip, useToast } from './ui';
import { ContractType, DocStatus } from '../types';
import type { Contract, LoaInstallment, Client, Machine } from '../types';
import {
  loaService,
  LOAContract,
  clientsService,
  stockService,
} from '../src/services';
import { usePaymentMethods } from '../src/hooks/usePaymentMethods';
import { PaymentMethodSelect, PaymentDetails, EMPTY_PAYMENT_DETAILS, isPaymentValid } from './PaymentFields';
import apiClient from '../src/services/apiClient';

// Transform API client to local format
const transformClient = (c: any): Client => ({
  id: c.id,
  code: c.code || '',
  name: c.nom || c.contact_principal || '',
  company: c.raison_sociale || '',
  type: c.type_client || 'PROFESSIONNEL',
  provenance: c.provenance_libelle || '',
  email: c.email || '',
  phone: c.telephone || '',
  address: c.adresse || '',
  city: c.ville || '',
  isVatExempt: c.is_exonere || false,
  isActive: c.is_active !== false,
  solvencyScore: c.score_solvabilite || 50,
});

// Transform API LOA contract to local format
const transformLoaContract = (c: LOAContract): Contract & { uuid: string; apiStatus: string; loaMonthlyPayment: number; loaInstallmentsPaid: number } => ({
  id: c.code || c.id,
  uuid: c.id,
  type: ContractType.LOA,
  clientId: c.client_id,
  clientName: c.client_nom,
  machineId: c.machine_id,
  machineName: c.machine_nom,
  startDate: c.date_debut,
  endDate: c.date_fin || '',
  status: mapLoaStatus(c.status),
  totalAmount: c.montant_total_finance || 0,
  depositAmount: c.apport_initial || 0,
  depositStatus: c.apport_paye ? 'PAID' : 'PENDING',
  loaDurationMonths: c.duree_mois,
  loaInterestRate: c.taux_interet_annuel,
  loaResidualValue: c.valeur_residuelle,
  installments: [], // Installments loaded separately if needed
  apiStatus: c.status,
  loaMonthlyPayment: c.montant_mensualite || 0,
  loaInstallmentsPaid: c.nombre_echeances_payees || 0,
});

// Map LOA status to DocStatus
const mapLoaStatus = (status: string): DocStatus => {
  const statusMap: Record<string, DocStatus> = {
    'DRAFT': DocStatus.DRAFT,
    'PENDING_APPROVAL': DocStatus.PENDING_APPROVAL,
    'VALIDATED': DocStatus.VALIDATED,
    'CAUTION_PAYEE': DocStatus.CAUTION_PAYEE,
    'ACTIVE': DocStatus.ACTIVE,
    'CLOSED': DocStatus.CLOSED,
    'DEFAULTED': DocStatus.DEFAULTED,
    'CANCELLED': DocStatus.CANCELLED,
  };
  return statusMap[status] || DocStatus.DRAFT;
};

// Track original API status for DEFAULTED distinction
const isDefaulted = (apiStatus: string): boolean => apiStatus === 'DEFAULTED';

// Transform API machine/product to local format
const transformMachine = (p: any): Machine => ({
  id: p.id,
  name: p.designation,
  serial: p.numero_serie || '',
  category: p.categorie_nom || '',
  status: p.status || 'AVAILABLE',
  location: 'Cotonou',
  dailyRate: p.tarif_location_journalier || 0,
  purchasePrice: p.prix_vente_public || 0,
  imageUrl: p.image_url || '',
  stockTotal: p.stock_total ?? 0,
});

const formatCurrency = (val: number) => new Intl.NumberFormat('fr-BJ', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(val);

// --- Badges ---
const InstallmentStatusBadge = ({ status }: { status: string }) => {
  const styles = {
    'PAID': 'green',
    'PENDING': 'gray',
    'OVERDUE': 'red',
    'PARTIAL': 'yellow',
  };
  const labels = {
    'PAID': 'Payé',
    'PENDING': 'À venir',
    'OVERDUE': 'En retard',
    'PARTIAL': 'Partiel',
  };
  return <Badge color={styles[status as keyof typeof styles] as any}>{labels[status as keyof typeof labels]}</Badge>;
};

const ContractStatusBadge = ({ status, apiStatus }: { status: DocStatus; apiStatus?: string }) => {
  const styles = {
    [DocStatus.DRAFT]: 'yellow',
    [DocStatus.PENDING_APPROVAL]: 'orange',
    [DocStatus.VALIDATED]: 'blue',
    [DocStatus.CAUTION_PAYEE]: 'purple',
    [DocStatus.ACTIVE]: 'green',
    [DocStatus.CLOSED]: 'gray',
    [DocStatus.DEFAULTED]: 'red',
    [DocStatus.CANCELLED]: 'red',
  };
  const labels: Record<string, string> = {
    [DocStatus.DRAFT]: 'Brouillon',
    [DocStatus.PENDING_APPROVAL]: 'En attente',
    [DocStatus.VALIDATED]: 'Validé',
    [DocStatus.CAUTION_PAYEE]: 'Apport payé',
    [DocStatus.ACTIVE]: 'Actif',
    [DocStatus.CLOSED]: 'Clôturé',
    [DocStatus.DEFAULTED]: 'Défaillant',
    [DocStatus.CANCELLED]: 'Annulé',
  };
  return <Badge color={styles[status] as any}>{labels[status] || status}</Badge>;
};

// --- Wizard: Create LOA ---

interface CreateLoaWizardProps {
  onClose: () => void;
  onSave: (contract: Partial<Contract>) => void;
  isSaving?: boolean;
}

const CreateLoaWizard = ({ onClose, onSave, isSaving }: CreateLoaWizardProps) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    clientId: '',
    machineId: '',
    machinePrice: 0,
    depositAmount: 0,
    durationMonths: 12,
    interestRate: 5, // %
    residualValue: 0,
    startDate: new Date().toISOString().split('T')[0],
  });

  const [simulatedSchedule, setSimulatedSchedule] = useState<LoaInstallment[]>([]);
  const [monthlyPayment, setMonthlyPayment] = useState(0);

  // Data from API
  const [machines, setMachines] = useState<Machine[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch machines and clients on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [machinesRes, clientsRes] = await Promise.all([
          stockService.listStock({ type: 'MACHINE', page_size: 100 }).catch(() => ({ data: [] })),
          clientsService.list({ page_size: 100 }).catch(() => ({ data: [] })),
        ]);
        setMachines((machinesRes.data || []).map((s: any) => transformMachine({
          id: s.id,
          designation: s.designation,
          numero_serie: s.numero_serie,
          categorie_nom: s.categorie_nom,
          status: s.status,
          tarif_location_journalier: s.tarif_location_journalier || 0,
          prix_vente_public: s.prix_vente_public || 0,
          stock_total: s.stock_total || 0,
        })));
        setClients((clientsRes.data || []).map(transformClient));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const selectedMachine = machines.find(m => m.id === formData.machineId);
  const selectedClient = clients.find(c => c.id === formData.clientId);

  // Auto-calculate simulation when key parameters change
  useEffect(() => {
    if (formData.machinePrice > 0) {
      calculateSchedule();
    }
  }, [formData.machinePrice, formData.depositAmount, formData.durationMonths, formData.interestRate, formData.residualValue, formData.startDate]);


  // Minimum deposit rate: 30% for Particulier, 20% for others
  const getMinDepositRate = (client?: typeof selectedClient) => {
    if (!client) return 0.2;
    return client.type === 'PARTICULIER' ? 0.3 : 0.2;
  };

  const minDepositRate = getMinDepositRate(selectedClient);
  const minDepositPercent = minDepositRate * 100;

  const handleMachineSelect = (m: Machine) => {
    const rate = getMinDepositRate(selectedClient);
    setFormData({
      ...formData,
      machineId: m.id,
      machinePrice: m.purchasePrice,
      depositAmount: Math.round(m.purchasePrice * rate),
      residualValue: m.purchasePrice * 0.05 // Default 5% residual
    });
  };

  const calculateSchedule = () => {
    // French amortization (constant annuity) — matches backend formula
    const principal = formData.machinePrice - formData.depositAmount;
    const n = formData.durationMonths;
    const r = formData.interestRate / 100 / 12; // Monthly rate

    // M = P × [r(1+r)^n] / [(1+r)^n - 1]
    let monthly: number;
    if (r > 0 && n > 0) {
      const onePlusRN = Math.pow(1 + r, n);
      monthly = principal * (r * onePlusRN) / (onePlusRN - 1);
    } else if (n > 0) {
      monthly = principal / n;
    } else {
      monthly = 0;
    }

    setMonthlyPayment(Math.round(monthly));

    // Generate amortization schedule
    const schedule: LoaInstallment[] = [];
    const startDate = new Date(formData.startDate);
    let capitalRestant = principal;

    for (let i = 1; i <= n; i++) {
      const dueDate = new Date(startDate);
      dueDate.setMonth(startDate.getMonth() + i);

      const interestPart = capitalRestant * r;
      const isLast = i === n;
      const principalPart = isLast ? capitalRestant : monthly - interestPart;
      const effectiveAmount = isLast ? capitalRestant + interestPart : monthly;
      capitalRestant -= principalPart;

      schedule.push({
        id: i,
        dueDate: dueDate.toISOString().split('T')[0],
        amount: Math.round(effectiveAmount),
        interestPart: Math.round(interestPart),
        principalPart: Math.round(principalPart),
        status: 'PENDING',
        paidAmount: 0
      });
    }

    setSimulatedSchedule(schedule);
  };

  const handleSave = () => {
    onSave({
      type: ContractType.LOA,
      clientId: formData.clientId,
      clientName: selectedClient?.company || selectedClient?.name,
      machineId: formData.machineId,
      machineName: selectedMachine?.name,
      startDate: formData.startDate,
      endDate: simulatedSchedule[simulatedSchedule.length - 1]?.dueDate || formData.startDate,
      status: DocStatus.VALIDATED,
      totalAmount: formData.machinePrice,
      depositAmount: formData.depositAmount,
      depositStatus: 'PENDING',
      loaDurationMonths: formData.durationMonths,
      loaInterestRate: formData.interestRate,
      loaResidualValue: formData.residualValue,
      installments: simulatedSchedule
    });
    // Don't call onClose() here - let the parent handle closing after API call completes
  };

  const depositPercent = formData.machinePrice > 0 ? (formData.depositAmount / formData.machinePrice) * 100 : 0;

  return (
    <Modal isOpen={true} onClose={onClose} title="Nouveau Contrat LOA">
      <div className="space-y-6">
        {/* Progress */}
        <div className="flex border-b border-slate-200 mb-4">
          {[1, 2, 3].map(s => (
            <div key={s} className={`flex-1 py-2 text-center text-xs font-medium border-b-2 ${step === s ? 'border-primary-500 text-primary-700' : 'border-transparent text-slate-400'}`}>
              Etape {s}: {s === 1 ? 'Simulation' : s === 2 ? 'Échéancier' : 'Validation'}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
             {isLoading ? (
               <div className="flex justify-center py-8">
                 <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
               </div>
             ) : (
             <div className="grid grid-cols-2 gap-4">
               <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Client <span className="text-red-500 ml-0.5">*</span></label>
                  <select
                    className="w-full border-slate-300 rounded-md text-sm py-2"
                    value={formData.clientId}
                    onChange={e => {
                      const client = clients.find(c => c.id === e.target.value);
                      const rate = getMinDepositRate(client);
                      const newDeposit = formData.machinePrice > 0 ? Math.round(formData.machinePrice * rate) : 0;
                      setFormData({...formData, clientId: e.target.value, depositAmount: newDeposit});
                    }}
                  >
                    <option value="">Choisir Client...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
                  </select>
                  <FieldHint>Titulaire du contrat LOA</FieldHint>
               </div>
               <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Machine <span className="text-red-500 ml-0.5">*</span></label>
                  <select
                    className="w-full border-slate-300 rounded-md text-sm py-2"
                    value={formData.machineId}
                    onChange={e => {
                      const m = machines.find(mach => mach.id === e.target.value);
                      if (m) handleMachineSelect(m);
                    }}
                  >
                    <option value="">Choisir Machine...</option>
                    {machines.filter(m => {
                      const isSerialized = !!m.serial;
                      if (isSerialized) return m.status !== 'SOLD' && m.status !== 'MAINTENANCE' && m.status !== 'LOA';
                      return (m.stockTotal ?? 0) > 0;
                    }).map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({formatCurrency(m.purchasePrice)}){m.status !== 'AVAILABLE' && !!m.serial ? ` [${m.status === 'RENTED' ? 'En location' : m.status === 'RESERVED' ? 'Réservée' : m.status}]` : ''}</option>
                    ))}
                  </select>
                  <FieldHint>Le prix de vente public détermine le montant financé</FieldHint>
               </div>
             </div>
             )}

             <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-4 animate-in fade-in">
                <div className="flex justify-between items-center text-sm">
                   <span className="text-slate-600">Prix de la machine:</span>
                   <span className="font-bold text-slate-900">{formatCurrency(formData.machinePrice)}</span>
                </div>
                
                <div>
                   <label className="block text-sm font-medium text-slate-700 mb-1">Apport Initial (Caution) <span className="text-red-500 ml-0.5">*</span> <InfoTooltip text={`Montant versé à la signature. Minimum : ${minDepositPercent}% (${selectedClient?.type === 'PARTICULIER' ? 'Particulier' : 'Professionnel/Entreprise'})`} /></label>
                   <Input
                     type="number"
                     min={0}
                     max={formData.machinePrice}
                     value={formData.depositAmount}
                     onChange={e => {
                       const val = Math.max(0, parseFloat(e.target.value) || 0);
                       const minDeposit = Math.round(formData.machinePrice * minDepositRate);
                       setFormData({...formData, depositAmount: Math.max(val, minDeposit)});
                     }}
                   />
                   <div className="flex justify-between mt-1 text-xs">
                     <span className={`${depositPercent < minDepositPercent ? 'text-red-500 font-bold' : 'text-green-600'}`}>
                       {depositPercent.toFixed(1)}% du prix
                     </span>
                     <span className="text-slate-400">Min. requis: {minDepositPercent}% ({selectedClient?.type === 'PARTICULIER' ? 'Particulier' : 'Pro/Entreprise'})</span>
                   </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                   <div className="col-span-1">
                     <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Durée (Mois) <span className="text-red-500 ml-0.5">*</span></label>
                     <Input
                       type="number" value={formData.durationMonths}
                       onChange={e => setFormData({...formData, durationMonths: parseInt(e.target.value)})}
                       hint="3 à 60 mois"
                     />
                   </div>
                   <div className="col-span-1">
                     <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Taux Intérêt % <span className="text-red-500 ml-0.5">*</span></label>
                     <Input
                       type="number" value={formData.interestRate}
                       min={0}
                       max={100}
                       onChange={e => setFormData({...formData, interestRate: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0))})}
                       hint="Taux annuel (0-100%)"
                     />
                   </div>
                   <div className="col-span-1">
                     <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Option Achat <InfoTooltip text="Montant à payer en fin de contrat pour devenir propriétaire de la machine" /></label>
                     <Input
                       type="number" value={formData.residualValue}
                       onChange={e => setFormData({...formData, residualValue: parseFloat(e.target.value)})}
                       hint="Valeur résiduelle en fin de contrat"
                     />
                   </div>
                </div>

                <div>
                   <label className="block text-sm font-medium text-slate-700 mb-1">Date 1ère échéance <span className="text-red-500 ml-0.5">*</span></label>
                   <Input
                     type="date" value={formData.startDate}
                     min={new Date().toISOString().split('T')[0]}
                     onChange={e => setFormData({...formData, startDate: e.target.value})}
                   />
                </div>
             </div>
            
             {/* Immediate Feedback Box */}
             <div className="bg-blue-600 text-white p-4 rounded-lg shadow-sm flex justify-between items-center animate-pulse-once transition-all">
                <div>
                  <p className="text-xs text-blue-100 uppercase font-bold">Mensualité Estimée</p>
                  <p className="text-2xl font-bold">{formatCurrency(monthlyPayment)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-blue-100 uppercase">Coût Total Crédit</p>
                  <p className="text-sm font-bold opacity-90">{formatCurrency((monthlyPayment * formData.durationMonths) - (formData.machinePrice - formData.depositAmount))}</p>
                </div>
             </div>

             <div className="flex gap-3 pt-2">
               <Button variant="ghost" onClick={onClose} className="flex-1">Annuler</Button>
               <Button onClick={() => setStep(2)} className="flex-1" disabled={!formData.clientId || !formData.machineId}>Voir Échéancier</Button>
             </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
             <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">N°</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-right">Montant</th>
                      <th className="px-3 py-2 text-right text-slate-400">Principal</th>
                      <th className="px-3 py-2 text-right text-slate-400">Intérêt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {simulatedSchedule.map((inst) => (
                      <tr key={inst.id}>
                        <td className="px-3 py-2 font-medium">{inst.id}</td>
                        <td className="px-3 py-2">{inst.dueDate}</td>
                        <td className="px-3 py-2 text-right font-bold">{formatCurrency(inst.amount)}</td>
                        <td className="px-3 py-2 text-right text-slate-500 text-xs">{formatCurrency(inst.principalPart)}</td>
                        <td className="px-3 py-2 text-right text-slate-500 text-xs">{formatCurrency(inst.interestPart)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             </div>

             <div className="flex gap-3 pt-2">
               <Button variant="ghost" onClick={() => setStep(1)} className="flex-1">Retour</Button>
               <Button onClick={() => setStep(3)} className="flex-1">Valider Simulation</Button>
             </div>
          </div>
        )}

        {step === 3 && (
           <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                 <Briefcase className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">Confirmer le contrat LOA</h3>
                <p className="text-slate-500 text-sm mt-1">
                  Client: <b>{selectedClient?.company || selectedClient?.name}</b><br/>
                  Machine: <b>{selectedMachine?.name}</b><br/>
                  Durée: <b>{formData.durationMonths} mois</b>
                </p>
              </div>

              <div className="bg-yellow-50 text-yellow-800 text-sm p-3 rounded border border-yellow-200 text-left">
                 <p className="font-bold flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Action requise après création:</p>
                 <ul className="list-disc list-inside mt-1 ml-1">
                   <li>Encaisser l'apport initial ({formatCurrency(formData.depositAmount)})</li>
                   <li>Imprimer et faire signer le contrat</li>
                   <li>Effectuer l'état des lieux de sortie</li>
                 </ul>
              </div>

              <div className="flex gap-3 pt-2">
               <Button variant="ghost" onClick={() => setStep(2)} className="flex-1">Retour</Button>
               <Button onClick={handleSave} className="flex-1" loading={isSaving}>Créer Contrat</Button>
             </div>
           </div>
        )}
      </div>
    </Modal>
  );
};

// --- LOA Detail View ---

interface LoaDetailProps {
  contract: Contract;
  onClose: () => void;
  onRefresh?: () => void;
  userRole?: string;
}

const LoaDetail = ({ contract, onClose, onRefresh, userRole }: LoaDetailProps) => {
  const paymentMethods = usePaymentMethods();
  const isMagasinier = userRole === 'MAGASINIER';
  // MAGASINIER defaults to 'info' tab and can only see info/history (no financial details)
  const [activeTab, setActiveTab] = useState<'info' | 'schedule' | 'history'>(isMagasinier ? 'info' : 'schedule');
  const [payingInstallmentId, setPayingInstallmentId] = useState<string | number | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState<LoaInstallment | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showApportModal, setShowApportModal] = useState(false);
  const [apportDetailForm, setApportDetailForm] = useState<PaymentDetails>({ ...EMPTY_PAYMENT_DETAILS });
  const { toast } = useToast();

  const handleConfirmApportDetail = async () => {
    if (!isPaymentValid(apportDetailForm)) return;
    setActionLoading(true);
    try {
      const contractUuid = (contract as any).uuid || contract.id;
      await loaService.markApportPaid(contractUuid, {
        mode_paiement: apportDetailForm.mode_paiement,
        reference: apportDetailForm.reference || undefined,
        banque: apportDetailForm.banque || undefined,
        numero_telephone: apportDetailForm.numero_telephone || undefined,
        transaction_id: apportDetailForm.transaction_id || undefined,
      } as any);
      toast('Apport initial enregistré comme payé', 'success');
      setShowApportModal(false);
      if (onRefresh) onRefresh();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message || 'Erreur';
      toast('Erreur: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)), 'error');
    } finally { setActionLoading(false); }
  };

  const handleActivate = async () => {
    setActionLoading(true);
    try {
      const contractUuid = (contract as any).uuid || contract.id;
      await loaService.activateContract(contractUuid);
      toast('Contrat LOA activé avec succès', 'success');
      if (onRefresh) onRefresh();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message || 'Impossible d\'activer';
      toast('Erreur: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransfer = async () => {
    setActionLoading(true);
    try {
      const contractUuid = (contract as any).uuid || contract.id;
      await loaService.transferOwnership(contractUuid, {
        date_transfert: new Date().toISOString().split('T')[0],
        paiement_option_achat: contract.loaResidualValue || 0,
        methode_paiement: 'VIREMENT',
      });
      toast('Transfert de propriété effectué avec succès', 'success');
      if (onRefresh) onRefresh();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message || 'Impossible de transférer';
      toast('Erreur: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)), 'error');
    } finally {
      setActionLoading(false);
    }
  };
  const [paymentForm, setPaymentForm] = useState({
    montant: 0,
    methode_paiement: 'ESPECES' as 'ESPECES' | 'CHEQUE' | 'VIREMENT' | 'MOBILE_MONEY_MTN' | 'MOBILE_MONEY_MOOV',
    reference: '',
  });

  // Machine details
  const [machineDetails, setMachineDetails] = useState<any>(null);

  // Audit history
  const [auditHistory, setAuditHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Fetch installments from API
  const [installments, setInstallments] = useState<LoaInstallment[]>([]);
  const [installmentsLoading, setInstallmentsLoading] = useState(true);

  const fetchInstallments = useCallback(async () => {
    setInstallmentsLoading(true);
    try {
      const contractUuid = (contract as any).uuid || contract.id;
      const apiInstallments = await loaService.getInstallments(contractUuid);
      setInstallments(apiInstallments.map((inst: any) => ({
        id: inst.id, // Real UUID from backend
        dueDate: inst.date_echeance,
        amount: inst.montant_echeance,
        interestPart: inst.part_interets,
        principalPart: inst.part_capital,
        status: inst.status as 'PAID' | 'OVERDUE' | 'PARTIAL' | 'PENDING',
        paidAmount: inst.montant_paye || 0,
        paymentDate: inst.date_paiement,
      })));
    } catch (err) {
      console.error('Error fetching installments:', err);
      setInstallments(contract.installments || []);
    } finally {
      setInstallmentsLoading(false);
    }
  }, [contract]);

  useEffect(() => {
    fetchInstallments();
  }, [fetchInstallments]);

  // Fetch machine details
  useEffect(() => {
    if (!contract.machineId) return;
    apiClient.get(`/products/${contract.machineId}`)
      .then((res: any) => {
        const data = res.data?.data || res.data;
        if (data) setMachineDetails(data);
      })
      .catch((err: any) => console.error('Failed to load machine details:', err?.response?.status));
  }, [contract.machineId]);

  // Fetch audit history for this contract
  useEffect(() => {
    const contractUuid = (contract as any).uuid || contract.id;
    setHistoryLoading(true);
    // Use LOA-specific endpoint or audit endpoint
    loaService.getContract(contractUuid)
      .then((fullContract: any) => {
        // Build history from contract data (status changes, payments, etc.)
        const history: any[] = [];
        if (fullContract.created_at) {
          history.push({ action: 'Création', description: `Contrat LOA créé - ${fullContract.code}`, created_at: fullContract.created_at, user_nom: fullContract.commercial_nom || 'Système' });
        }
        if (fullContract.status === 'ACTIVE' || fullContract.status === 'CLOSED') {
          history.push({ action: 'Activation', description: 'Contrat activé', created_at: fullContract.updated_at, user_nom: 'Système' });
        }
        if (fullContract.option_achat_levee && fullContract.date_levee_option) {
          history.push({ action: 'Transfert', description: 'Option d\'achat levée - Transfert de propriété', created_at: fullContract.date_levee_option, user_nom: 'Système' });
        }
        setAuditHistory(history.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
      })
      .catch(() => setAuditHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [contract]);

  // Stats from fetched installments
  const paidInstallments = installments.filter(i => i.status === 'PAID').length;
  const totalInstallments = installments.length;
  const progress = totalInstallments > 0 ? (paidInstallments / totalInstallments) * 100 : 0;

  const overdueAmount = installments.filter(i => i.status === 'OVERDUE').reduce((acc, curr) => acc + curr.amount, 0);

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[800px] bg-white shadow-2xl z-40 flex flex-col border-l border-slate-200">
      {/* Header */}
      <div className="px-6 py-6 border-b border-slate-200 bg-slate-50">
         <div className="flex justify-between items-start">
            <div>
               <div className="flex items-center gap-2 mb-1">
                 <Badge color="purple">LOA / LEASING</Badge>
                 <ContractStatusBadge status={contract.status} />
               </div>
               <h2 className="text-2xl font-bold text-slate-900">{contract.id}</h2>
               <p className="text-slate-600 font-medium">{contract.clientName} • {contract.machineName}</p>
            </div>
            <div className="flex items-center gap-2">
              {contract.status === DocStatus.VALIDATED && (userRole === 'COMPTABLE' || userRole === 'ADMIN') && (
                <Button size="sm" variant="primary" icon={DollarSign} onClick={() => {
                  setApportDetailForm({ ...EMPTY_PAYMENT_DETAILS });
                  setShowApportModal(true);
                }} loading={actionLoading}>Enregistrer apport</Button>
              )}
              {contract.status === DocStatus.VALIDATED && userRole !== 'COMPTABLE' && userRole !== 'ADMIN' && (
                <span className="text-xs text-amber-600 font-medium px-2 py-1 bg-amber-50 rounded">En attente apport</span>
              )}
              {contract.status === DocStatus.CAUTION_PAYEE && (userRole === 'ADMIN' || userRole === 'MAGASINIER') && (
                <Button size="sm" variant="primary" icon={CheckCircle} onClick={handleActivate} loading={actionLoading}>Activer</Button>
              )}
              {contract.status === DocStatus.ACTIVE && (userRole === 'ADMIN' || userRole === 'GERANT') && progress >= 100 && (
                <Button size="sm" variant="primary" icon={Briefcase} onClick={handleTransfer} loading={actionLoading}>Transférer Propriété</Button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
         </div>

         {/* Progress Bar - hidden for MAGASINIER */}
         {!isMagasinier && (
         <div className="mt-6">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Progression remboursement ({paidInstallments}/{totalInstallments})</span>
              <span>{progress.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div className="bg-purple-600 h-2 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
            </div>
         </div>
         )}
      </div>

      {/* Tabs */}
      <div className="px-6 border-b border-slate-200">
        <nav className="-mb-px flex space-x-6">
          {(isMagasinier ? ['info', 'history'] : ['schedule', 'info', 'history']).map((tab) => (
             <button
               key={tab}
               onClick={() => setActiveTab(tab as any)}
               className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                 activeTab === tab
                   ? 'border-purple-500 text-purple-600'
                   : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
               }`}
             >
               {tab === 'schedule' && 'Échéancier & Paiements'}
               {tab === 'info' && 'Infos Générales'}
               {tab === 'history' && 'Historique Actions'}
             </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
         {activeTab === 'schedule' && (
           <div className="space-y-6">
              {overdueAmount > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r shadow-sm flex justify-between items-center">
                   <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="font-bold text-red-900">Attention : Retard de paiement</p>
                        <p className="text-sm text-red-700">Montant total dû : {formatCurrency(overdueAmount)}</p>
                      </div>
                   </div>
                   <Button size="sm" variant="danger" icon={Mail} onClick={() => {
                     toast(`Un email de relance sera envoyé à ${contract.clientName} pour le montant de ${formatCurrency(overdueAmount)}`, 'info');
                   }}>Relancer Client</Button>
                </div>
              )}

              <Card>
                 <table className="min-w-full divide-y divide-slate-200 text-sm">
                   <thead className="bg-slate-50">
                     <tr>
                       <th className="px-4 py-3 text-left font-medium text-slate-500">Date Due</th>
                       <th className="px-4 py-3 text-right font-medium text-slate-500">Montant</th>
                       <th className="px-4 py-3 text-center font-medium text-slate-500">Statut</th>
                       <th className="px-4 py-3 text-right font-medium text-slate-500">Action</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-200 bg-white">
                     {installmentsLoading ? (
                       <tr><td colSpan={4} className="text-center py-8 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Chargement des échéances...</td></tr>
                     ) : installments.length === 0 ? (
                       <tr><td colSpan={4} className="text-center py-8 text-slate-500">Aucune échéance trouvée</td></tr>
                     ) : installments.map((inst) => (
                       <tr key={inst.id} className="hover:bg-slate-50">
                         <td className="px-4 py-3 text-slate-900 font-medium">
                            {inst.dueDate}
                            {inst.status === 'OVERDUE' && <span className="text-xs text-red-500 block">Retard</span>}
                         </td>
                         <td className="px-4 py-3 text-right text-slate-700">{formatCurrency(inst.amount)}</td>
                         <td className="px-4 py-3 text-center"><InstallmentStatusBadge status={inst.status} /></td>
                         <td className="px-4 py-3 text-right">
                           {(inst.status === 'PENDING' || inst.status === 'OVERDUE') && (userRole === 'ADMIN' || userRole === 'COMPTABLE') && (
                              <Button size="sm" variant="secondary" icon={CreditCard} onClick={() => {
                                setSelectedInstallment(inst);
                                setPaymentForm({ montant: inst.amount, methode_paiement: 'ESPECES', reference: '' });
                                setShowPaymentModal(true);
                              }}>Payer</Button>
                           )}
                           {inst.status === 'PAID' && <span className="text-xs text-slate-400">Payé le {inst.paymentDate}</span>}
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
              </Card>
           </div>
         )}

         {activeTab === 'info' && (
           <div className="space-y-4">
              <div className={`grid ${isMagasinier ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                {!isMagasinier && (
                <Card title="Conditions Financières">
                   <div className="space-y-3 text-sm">
                      <div className="flex justify-between"><span>Montant Financé:</span> <span className="font-medium">{formatCurrency((contract.totalAmount - (contract.loaResidualValue || 0)))}</span></div>
                      <div className="flex justify-between"><span>Apport:</span> <span className={`font-medium ${contract.depositStatus === 'PAID' ? 'text-green-600' : 'text-amber-600'}`}>{formatCurrency(contract.depositAmount)} {contract.depositStatus === 'PAID' ? '(Payé)' : '(Non payé)'}</span></div>
                      <div className="flex justify-between"><span>Option Achat:</span> <span className="font-medium text-slate-600">{formatCurrency(contract.loaResidualValue || 0)}</span></div>
                      <div className="flex justify-between"><span>Taux Intérêt:</span> <span className="font-medium">{contract.loaInterestRate}%</span></div>
                      <div className="flex justify-between"><span>Durée:</span> <span className="font-medium">{contract.loaDurationMonths} mois</span></div>
                   </div>
                </Card>
                )}
                <Card title="Détails du Contrat">
                   <div className="space-y-3 text-sm">
                      <div className="flex justify-between"><span>Code:</span> <span className="font-medium">{contract.id}</span></div>
                      <div className="flex justify-between"><span>Client:</span> <span className="font-medium">{contract.clientName}</span></div>
                      <div className="flex justify-between"><span>Date début:</span> <span className="font-medium">{contract.startDate ? new Date(contract.startDate).toLocaleDateString('fr-FR') : '-'}</span></div>
                      <div className="flex justify-between"><span>Date fin:</span> <span className="font-medium">{contract.endDate ? new Date(contract.endDate).toLocaleDateString('fr-FR') : '-'}</span></div>
                      <div className="flex justify-between"><span>Statut:</span> <ContractStatusBadge status={contract.status} /></div>
                   </div>
                </Card>
              </div>
              <Card title="Machine">
                 <div className="space-y-3 text-sm">
                    <div className="flex justify-between"><span>Désignation:</span> <span className="font-bold text-slate-900">{contract.machineName}</span></div>
                    {machineDetails ? (
                      <>
                        {machineDetails.reference && <div className="flex justify-between"><span>Référence:</span> <span className="font-medium">{machineDetails.reference}</span></div>}
                        {machineDetails.numero_serie && <div className="flex justify-between"><span>N° de série:</span> <span className="font-medium">{machineDetails.numero_serie}</span></div>}
                        {machineDetails.categorie_nom && <div className="flex justify-between"><span>Catégorie:</span> <span className="font-medium">{machineDetails.categorie_nom}</span></div>}
                        {machineDetails.marque && <div className="flex justify-between"><span>Marque:</span> <span className="font-medium">{machineDetails.marque}</span></div>}
                        {machineDetails.modele && <div className="flex justify-between"><span>Modèle:</span> <span className="font-medium">{machineDetails.modele}</span></div>}
                        {machineDetails.annee_fabrication && <div className="flex justify-between"><span>Année:</span> <span className="font-medium">{machineDetails.annee_fabrication}</span></div>}
                        {machineDetails.status && <div className="flex justify-between"><span>État actuel:</span> <span className="font-medium">{machineDetails.status}</span></div>}
                      </>
                    ) : (
                      <p className="text-slate-400 text-xs">Chargement des détails...</p>
                    )}
                 </div>
              </Card>
           </div>
         )}

         {activeTab === 'history' && (
           <div className="space-y-4">
              {historyLoading ? (
                <div className="text-center py-12 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Chargement de l'historique...</div>
              ) : auditHistory.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucun historique disponible</p>
                  <p className="text-xs text-slate-400 mt-1">Les actions sur ce contrat apparaîtront ici</p>
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                  {auditHistory.map((log: any, idx: number) => (
                    <div key={log.id || idx} className="relative pl-10 pb-6">
                      <div className="absolute left-2.5 top-1 w-3 h-3 rounded-full bg-purple-500 border-2 border-white"></div>
                      <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{log.action || log.type}</p>
                            <p className="text-sm text-slate-600 mt-0.5">{log.description || log.details || ''}</p>
                          </div>
                          <span className="text-xs text-slate-400 whitespace-nowrap ml-4">{log.created_at ? new Date(log.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><User className="w-3 h-3" />{log.user_nom || log.user_email || 'Système'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
           </div>
         )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedInstallment && (
        <Modal isOpen={true} onClose={() => setShowPaymentModal(false)} title={`Payer Échéance #${selectedInstallment.id}`}>
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded text-sm">
              <div className="flex justify-between"><span>Montant dû:</span> <b>{formatCurrency(selectedInstallment.amount)}</b></div>
              <div className="flex justify-between"><span>Date échéance:</span> <span>{selectedInstallment.dueDate}</span></div>
            </div>
            <Input
              label="Montant à payer"
              type="number"
              value={paymentForm.montant}
              onChange={e => setPaymentForm({...paymentForm, montant: parseFloat(e.target.value)})}
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mode de paiement</label>
              <select
                className="w-full border-slate-300 rounded-md py-2"
                value={paymentForm.methode_paiement}
                onChange={e => setPaymentForm({...paymentForm, methode_paiement: e.target.value as any})}
              >
                {paymentMethods.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
              </select>
            </div>
            <Input
              label="Référence"
              placeholder="N° chèque, référence virement..."
              value={paymentForm.reference}
              onChange={e => setPaymentForm({...paymentForm, reference: e.target.value})}
            />
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="ghost" onClick={() => setShowPaymentModal(false)}>Annuler</Button>
              <Button
                disabled={payingInstallmentId !== null}
                onClick={async () => {
                  setPayingInstallmentId(selectedInstallment.id);
                  try {
                    await loaService.payInstallment({
                      echeance_id: String(selectedInstallment.id),
                      montant: paymentForm.montant,
                      methode_paiement: paymentForm.methode_paiement,
                      reference: paymentForm.reference || undefined,
                    });
                    setShowPaymentModal(false);
                    toast('Paiement enregistré avec succès', 'success');
                    fetchInstallments();
                    if (onRefresh) onRefresh();
                  } catch (err: any) {
                    toast('Erreur: ' + (err.message || 'Échec du paiement'), 'error');
                  } finally {
                    setPayingInstallmentId(null);
                  }
                }}
              >
                {payingInstallmentId !== null ? 'Traitement...' : 'Confirmer le paiement'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Apport Payment Modal */}
      {showApportModal && (
        <Modal isOpen={true} title="Enregistrer l'apport initial" onClose={() => setShowApportModal(false)}>
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Montant apport:</span> <span className="font-bold">{formatCurrency(contract.depositAmount || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Client:</span> <span>{contract.clientName}</span></div>
            </div>
            <PaymentMethodSelect value={apportDetailForm} onChange={setApportDetailForm} />
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="ghost" onClick={() => setShowApportModal(false)} disabled={actionLoading}>Annuler</Button>
              <Button variant="primary" onClick={handleConfirmApportDetail} disabled={actionLoading || !isPaymentValid(apportDetailForm)}>
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmer le paiement
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// --- Main LOA Module ---

export const LoaModule = ({ canWrite, userRole }: { canWrite?: boolean; userRole?: string }) => {
  const isMagasinier = userRole === 'MAGASINIER';
  const isCommercial = userRole === 'COMMERCIAL';
  const isManager = userRole === 'ADMIN' || userRole === 'GERANT';
  const isComptable = userRole === 'COMPTABLE' || userRole === 'ADMIN';
  const canWriteLoa = (canWrite ?? false) && !isMagasinier;
  const canPayLoa = userRole === 'ADMIN' || userRole === 'COMPTABLE';
  const paymentMethods = usePaymentMethods();
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Edit draft modal
  const [editingLoa, setEditingLoa] = useState<(Contract & { uuid: string }) | null>(null);
  const [editLoaForm, setEditLoaForm] = useState({ notes: '', conditions_particulieres: '' });
  const [editLoaLoading, setEditLoaLoading] = useState(false);

  // Apport payment modal
  const [apportContract, setApportContract] = useState<Contract | null>(null);
  const [apportForm, setApportForm] = useState<PaymentDetails>({ ...EMPTY_PAYMENT_DETAILS });
  const [apportLoading, setApportLoading] = useState(false);
  const { toast } = useToast();

  // Server-side pagination, sort, search
  const PAGE_SIZE = 20;
  const [loaPage, setLoaPage] = useState(1);
  const [loaPagination, setLoaPagination] = useState({ totalPages: 1, totalItems: 0 });

  type LoaSortKey = 'code' | 'clientName' | 'machineName' | 'dateDebut' | 'mensualite' | 'echeancesPayees' | 'status';
  const [loaSort, setLoaSort] = useState<{ key: LoaSortKey; dir: 'asc' | 'desc' }>({ key: 'dateDebut', dir: 'desc' });

  const sortKeyMap: Record<LoaSortKey, string> = {
    code: 'code', clientName: 'client_nom', machineName: 'machine_nom',
    dateDebut: 'date_debut', mensualite: 'montant_mensualite',
    echeancesPayees: 'nombre_echeances_payees', status: 'status',
  };

  const toggleLoaSort = (key: LoaSortKey) => {
    setLoaSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    setLoaPage(1);
  };

  const LoaSortIcon = ({ col }: { col: LoaSortKey }) => {
    if (loaSort.key !== col) return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-30" />;
    return loaSort.dir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-primary-600" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-primary-600" />;
  };

  // Debounce search (400ms)
  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedFilter(filter); setLoaPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [filter]);

  // API data
  const [loaContracts, setLoaContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ encours: 0, impayes: 0, tauxRecouvrement: 0 });

  // Fetch LOA contracts from API (server-side pagination)
  const fetchContracts = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page: loaPage,
        page_size: PAGE_SIZE,
        sort_by: sortKeyMap[loaSort.key],
        sort_dir: loaSort.dir,
      };
      if (debouncedFilter.trim()) params.search = debouncedFilter.trim();

      const response = await loaService.listContracts(params);
      const contracts = (response.data || []).map(transformLoaContract);
      setLoaContracts(contracts);
      setLoaPagination({
        totalPages: response.pagination.total_pages,
        totalItems: response.pagination.total_items,
      });

      // Calculate stats from API contract-level fields
      let totalEncours = 0;
      let totalImpayes = 0;
      let totalPaid = 0;
      let totalDue = 0;

      (response.data || []).forEach((c: any) => {
        const isActive = ['ACTIVE', 'VALIDATED', 'DEFAULTED'].includes(c.status);
        if (isActive) {
          const financed = parseFloat(c.montant_total_finance) || 0;
          const paid = parseFloat(c.montant_paye) || 0;
          totalEncours += financed - paid;
          totalDue += financed;
          totalPaid += paid;
          if (c.nombre_echeances_retard > 0) {
            totalImpayes += financed - paid;
          }
        }
      });

      setStats({
        encours: totalEncours,
        impayes: totalImpayes,
        tauxRecouvrement: totalDue > 0 ? Math.round((totalPaid / totalDue) * 100) : 0,
      });
    } catch (error) {
      console.error('Error fetching LOA contracts:', error);
    } finally {
      setIsLoading(false);
    }
  }, [loaPage, debouncedFilter, loaSort]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Handle save LOA contract
  const handleSaveLoa = async (contractData: Partial<Contract>) => {
    setIsSaving(true);
    try {
      // Transform to API format
      const createData = {
        client_id: contractData.clientId,
        machine_id: contractData.machineId,
        duree_mois: contractData.loaDurationMonths,
        taux_interet_annuel: contractData.loaInterestRate,
        apport_initial: contractData.depositAmount,
        valeur_residuelle: contractData.loaResidualValue,
        valeur_machine: contractData.totalAmount,
        date_debut: contractData.startDate,
      };

      await loaService.createContract(createData);

      toast('Contrat LOA créé avec succès', 'success');

      // Refresh contracts list
      await fetchContracts();

      // Close wizard
      setIsWizardOpen(false);
    } catch (error: any) {
      console.error('Error creating LOA contract:', error);
      const detail = error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Veuillez réessayer';
      toast(`Erreur lors de la création du contrat LOA: ${detail}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditLoaDraft = async (contract: Contract & { uuid: string }) => {
    try {
      const full = await loaService.getContract(contract.uuid);
      setEditLoaForm({
        notes: full.notes || '',
        conditions_particulieres: (full as any).conditions_particulieres || '',
      });
      setEditingLoa(contract);
    } catch (err: any) {
      toast('Erreur lors du chargement du contrat', 'error');
    }
  };

  const handleSaveLoaEdit = async () => {
    if (!editingLoa) return;
    setEditLoaLoading(true);
    try {
      await loaService.updateContract(editingLoa.uuid, {
        notes: editLoaForm.notes || undefined,
        conditions_particulieres: editLoaForm.conditions_particulieres || undefined,
      });
      toast('Contrat LOA modifié avec succès', 'success');
      setEditingLoa(null);
      fetchContracts();
    } catch (err: any) {
      toast('Erreur: ' + (err?.response?.data?.detail || err.message || 'Impossible de modifier'), 'error');
    } finally {
      setEditLoaLoading(false);
    }
  };

  const handleSubmitLoaForApproval = async (contract: Contract) => {
    try {
      await loaService.submitForApproval((contract as any).uuid || contract.id);
      toast('Contrat LOA soumis pour approbation', 'success');
      fetchContracts();
    } catch (err: any) {
      toast('Erreur: ' + (err?.response?.data?.detail || err.message || 'Impossible de soumettre'), 'error');
    }
  };

  const [approveTarget, setApproveTarget] = useState<Contract | null>(null);
  const [approveSiteId, setApproveSiteId] = useState('');
  const [approveSites, setApproveSites] = useState<{ id: string; nom: string; ville?: string }[]>([]);
  const [approving, setApproving] = useState(false);

  const openApproveModal = (contract: Contract) => {
    setApproveTarget(contract);
    setApproveSiteId('');
    stockService.listSites().then(setApproveSites).catch(() => setApproveSites([]));
  };

  const handleApproveLoa = async () => {
    if (!approveTarget) return;
    setApproving(true);
    try {
      await loaService.approveContract(
        (approveTarget as any).uuid || approveTarget.id,
        approveSiteId || undefined,
      );
      toast('Contrat LOA approuvé par le gérant', 'success');
      setApproveTarget(null);
      fetchContracts();
    } catch (err: any) {
      toast('Erreur: ' + (err?.response?.data?.detail || err?.response?.data?.message || err.message || 'Impossible d\'approuver'), 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleMarkApportPaid = (contract: Contract) => {
    setApportForm({ ...EMPTY_PAYMENT_DETAILS });
    setApportContract(contract);
  };

  const handleConfirmApportPaid = async () => {
    if (!apportContract || !isPaymentValid(apportForm)) return;
    setApportLoading(true);
    try {
      await loaService.markApportPaid((apportContract as any).uuid || apportContract.id, {
        mode_paiement: apportForm.mode_paiement,
        reference: apportForm.reference || undefined,
        banque: apportForm.banque || undefined,
        numero_telephone: apportForm.numero_telephone || undefined,
        transaction_id: apportForm.transaction_id || undefined,
      } as any);
      toast('Apport initial enregistré comme payé', 'success');
      setApportContract(null);
      fetchContracts();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message || 'Impossible d\'enregistrer';
      toast('Erreur: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)), 'error');
    } finally {
      setApportLoading(false);
    }
  };

  const handleActivateLoa = async (contract: Contract) => {
    try {
      await loaService.activateContract((contract as any).uuid || contract.id);
      toast('Contrat LOA activé avec succès', 'success');
      fetchContracts();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message || 'Impossible d\'activer';
      toast('Erreur: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)), 'error');
    }
  };

  const handleTransferLoa = async (contract: Contract) => {
    try {
      await loaService.transferOwnership((contract as any).uuid || contract.id, {
        date_transfert: new Date().toISOString().split('T')[0],
        paiement_option_achat: contract.loaResidualValue || 0,
        methode_paiement: 'VIREMENT',
      });
      toast('Transfert de propriété effectué avec succès', 'success');
      fetchContracts();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err.message || 'Impossible de transférer';
      toast('Erreur: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)), 'error');
    }
  };

  return (
    <div className="space-y-6">
       <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
           <h2 className="text-2xl font-bold text-slate-800">Gestion LOA / Crédit-Bail</h2>
           <p className="text-sm text-slate-500">Contrats de location avec option d'achat et suivi des échéances.</p>
        </div>
        {canWriteLoa && <Button icon={Plus} onClick={() => setIsWizardOpen(true)}>Nouveau Dossier LOA</Button>}
      </div>

      {/* Stats Cards — hidden for MAGASINIER */}
      {!isMagasinier && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-purple-50 border-purple-100">
           <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg text-purple-600"><Briefcase className="w-6 h-6" /></div>
              <div>
                 <p className="text-sm text-purple-700 font-medium">Encours Total</p>
                 <p className="text-2xl font-bold text-purple-900">{formatCurrency(stats.encours)}</p>
              </div>
           </div>
        </Card>
        <Card className="bg-red-50 border-red-100">
           <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg text-red-600"><AlertTriangle className="w-6 h-6" /></div>
              <div>
                 <p className="text-sm text-red-700 font-medium">Impayés (Retard)</p>
                 <p className="text-2xl font-bold text-red-900">{formatCurrency(stats.impayes)}</p>
              </div>
           </div>
        </Card>
         <Card className="bg-blue-50 border-blue-100">
           <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg text-blue-600"><TrendingUp className="w-6 h-6" /></div>
              <div>
                 <p className="text-sm text-blue-700 font-medium">Taux Recouvrement</p>
                 <p className="text-2xl font-bold text-blue-900">{stats.tauxRecouvrement}%</p>
              </div>
           </div>
        </Card>
      </div>
      )}

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
         <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <h3 className="font-bold text-slate-700">Dossiers en cours</h3>
            <div className="relative w-64">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input 
                className="pl-8 w-full border-slate-300 rounded-md text-sm py-1" 
                placeholder="Rechercher client, n°..." 
                value={filter}
                onChange={e => setFilter(e.target.value)}
              />
            </div>
         </div>
         {isLoading ? (
           <div className="flex justify-center py-12">
             <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
           </div>
         ) : (
         <>
         <table className="min-w-full divide-y divide-slate-200">
           <thead className="bg-slate-50">
             <tr>
               <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleLoaSort('code')}>N° Dossier<LoaSortIcon col="code" /></th>
               <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleLoaSort('clientName')}>Client<LoaSortIcon col="clientName" /></th>
               <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleLoaSort('machineName')}>Machine<LoaSortIcon col="machineName" /></th>
               <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleLoaSort('dateDebut')}>Début<LoaSortIcon col="dateDebut" /></th>
               {!isMagasinier && <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleLoaSort('mensualite')}>Mensualité<LoaSortIcon col="mensualite" /></th>}
               {!isMagasinier && <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleLoaSort('echeancesPayees')}>Échéances payées<LoaSortIcon col="echeancesPayees" /></th>}
               <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleLoaSort('status')}>Statut<LoaSortIcon col="status" /></th>
               <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Action</th>
             </tr>
           </thead>
           <tbody className="divide-y divide-slate-200">
             {loaContracts.map(c => (
               <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedContract(c)}>
                 <td className="px-6 py-4 text-sm font-medium text-purple-700">{c.id}</td>
                 <td className="px-6 py-4 text-sm text-slate-700 font-medium">{c.clientName}</td>
                 <td className="px-6 py-4 text-sm text-slate-600">{c.machineName}</td>
                 <td className="px-6 py-4 text-sm text-slate-600">{c.startDate}</td>
                 {!isMagasinier && <td className="px-6 py-4 text-sm text-right font-bold text-slate-900">{formatCurrency((c as any).loaMonthlyPayment || 0)}</td>}
                 {!isMagasinier && <td className="px-6 py-4 text-center text-sm text-slate-700">{(c as any).loaInstallmentsPaid || 0}/{c.loaDurationMonths || 0}</td>}
                 <td className="px-6 py-4 text-center"><ContractStatusBadge status={c.status} apiStatus={(c as any).apiStatus} /></td>
                 <td className="px-6 py-4 text-right">
                    {c.status === DocStatus.DRAFT && canWriteLoa && (
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleEditLoaDraft(c as any); }} icon={Pencil}>Modifier</Button>
                    )}
                    {c.status === DocStatus.DRAFT && canWriteLoa && (
                      <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleSubmitLoaForApproval(c); }} icon={CheckCircle}>Soumettre</Button>
                    )}
                    {c.status === DocStatus.PENDING_APPROVAL && isManager && (
                      <Button size="sm" variant="primary" onClick={(e) => { e.stopPropagation(); openApproveModal(c); }} icon={CheckCircle}>Approuver</Button>
                    )}
                    {c.status === DocStatus.VALIDATED && isComptable && (
                      <Button size="sm" variant="primary" onClick={(e) => { e.stopPropagation(); handleMarkApportPaid(c); }} icon={DollarSign}>Enregistrer apport</Button>
                    )}
                    {c.status === DocStatus.VALIDATED && !isComptable && (
                      <span className="text-xs text-amber-600 font-medium px-2 py-1 bg-amber-50 rounded">En attente apport</span>
                    )}
                    {c.status === DocStatus.CAUTION_PAYEE && (userRole === 'ADMIN' || userRole === 'MAGASINIER') && (
                      <Button size="sm" variant="primary" onClick={(e) => { e.stopPropagation(); handleActivateLoa(c); }} icon={CheckCircle}>Activer</Button>
                    )}
                    {c.status === DocStatus.ACTIVE && (
                      <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); setSelectedContract(c); }} icon={Eye}>Détails</Button>
                    )}
                    {c.status !== DocStatus.DRAFT && c.status !== DocStatus.PENDING_APPROVAL && c.status !== DocStatus.VALIDATED && c.status !== DocStatus.ACTIVE && <ChevronRight className="w-5 h-5 text-slate-400 inline" />}
                 </td>
               </tr>
             ))}
             {loaContracts.length === 0 && (
               <tr><td colSpan={isMagasinier ? 6 : 8} className="text-center py-8 text-slate-500">Aucun dossier LOA trouvé.</td></tr>
             )}
           </tbody>
         </table>
         {/* Pagination */}
         {loaPagination.totalPages > 1 && (
           <div className="px-4 py-3 flex items-center justify-between border-t border-slate-200">
             <div className="text-sm text-slate-500">
               {loaPagination.totalItems} dossier{loaPagination.totalItems > 1 ? 's' : ''}
             </div>
             <div className="flex items-center gap-2">
               <Button
                 variant="ghost"
                 size="sm"
                 disabled={loaPage <= 1}
                 onClick={() => setLoaPage(p => p - 1)}
               >
                 Précédent
               </Button>
               <span className="text-sm text-slate-600">
                 Page {loaPage} sur {loaPagination.totalPages}
               </span>
               <Button
                 variant="ghost"
                 size="sm"
                 disabled={loaPage >= loaPagination.totalPages}
                 onClick={() => setLoaPage(p => p + 1)}
               >
                 Suivant
               </Button>
             </div>
           </div>
         )}
         </>
         )}
      </div>

      {isWizardOpen && <CreateLoaWizard onClose={() => setIsWizardOpen(false)} onSave={handleSaveLoa} isSaving={isSaving} />}
      
      {selectedContract && (
        <>
          <div className="fixed inset-0 bg-slate-900/50 z-30" onClick={() => setSelectedContract(null)} />
          <LoaDetail contract={selectedContract} onClose={() => setSelectedContract(null)} onRefresh={fetchContracts} userRole={userRole} />
        </>
      )}

      {/* Apport Payment Modal */}
      {apportContract && (
        <Modal isOpen={true} title={`Enregistrer apport — ${apportContract.id}`} onClose={() => setApportContract(null)}>
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Montant apport:</span> <span className="font-bold">{formatCurrency(apportContract.depositAmount || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Client:</span> <span>{apportContract.clientName}</span></div>
            </div>
            <PaymentMethodSelect value={apportForm} onChange={setApportForm} />
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="ghost" onClick={() => setApportContract(null)} disabled={apportLoading}>Annuler</Button>
              <Button variant="primary" onClick={handleConfirmApportPaid} disabled={apportLoading || !isPaymentValid(apportForm)}>
                {apportLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmer le paiement
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit LOA Draft Modal */}
      {editingLoa && (
        <Modal isOpen={true} title={`Modifier le brouillon ${editingLoa.id}`} onClose={() => setEditingLoa(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <textarea className="w-full border border-slate-300 rounded-md text-sm p-2" rows={2}
                value={editLoaForm.notes} onChange={e => setEditLoaForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notes internes..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Conditions particulières</label>
              <textarea className="w-full border border-slate-300 rounded-md text-sm p-2" rows={3}
                value={editLoaForm.conditions_particulieres} onChange={e => setEditLoaForm(f => ({ ...f, conditions_particulieres: e.target.value }))}
                placeholder="Conditions spécifiques au contrat..." />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="ghost" onClick={() => setEditingLoa(null)} disabled={editLoaLoading}>Annuler</Button>
              <Button variant="primary" onClick={handleSaveLoaEdit} disabled={editLoaLoading}>
                {editLoaLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enregistrer
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {approveTarget && (
        <Modal isOpen={!!approveTarget} onClose={() => setApproveTarget(null)} title={`Approuver le contrat LOA ${approveTarget.id}`}>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              L'approbation génère automatiquement un Bon de Livraison.
              Choisissez le dépôt d'enlèvement de la machine.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Dépôt d'enlèvement</label>
              <select
                className="w-full border-slate-300 rounded-md py-2 text-sm"
                value={approveSiteId}
                onChange={e => setApproveSiteId(e.target.value)}
              >
                <option value="">Site par défaut</option>
                {approveSites.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.nom}{s.ville ? ` (${s.ville})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Prévente : l'approbation est toujours enregistrée. Le BL n'est créé que si le stock est suffisant au dépôt choisi; sinon il devra être créé manuellement après réception.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="ghost" onClick={() => setApproveTarget(null)} disabled={approving}>Annuler</Button>
              <Button variant="primary" onClick={handleApproveLoa} disabled={approving} icon={CheckCircle}>
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Approuver
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
